import {
  computed,
  defineComponent,
  reactive,
  watch,
} from '@vue/composition-api';
import TableFilter from '@/components/common/table-filter.component.vue'; // フィルタ用共通コンポーネント
import { SchoolHeader, TableSchool } from '@/shared/type';
import { useTable } from '@/hooks/useTable';
import { ResponseSchool } from '@/shared/types/entities/School';
import { toTableSchools } from '@/utils/transform';
import { schoolFilterFactory, SchoolListFilter } from '@/constants/initialData';
import Reset from '../reset.component.vue';
import { endPoints } from '@/shared/types/api';
import { axios } from '@/plugins/axios';
import { DatePicker, Dialog, Typography } from '@/components/parts';
import {
  tableFooterProps,
  tableHeaderClass,
} from '@/constants/masterData/v-data-table';
import { useSchool } from '@/shared/hooks/useSchool';
import { useDialog } from '@/hooks/useDialog';

// 学校一覧ヘッダ情報(初期表示ヘッダ)
const schoolHeaders: SchoolHeader[] = [
  {
    text: '',
    value: 'data-table-select',
    width: '1px',
    class: tableHeaderClass,
  },
  {
    text: '学校名称',
    value: 'name',
    width: '23%',
    class: tableHeaderClass,
  },
  {
    text: '公開期間（リハーサル）',
    value: 'rehearsalDate',
    width: '27%',
    class: tableHeaderClass,
  },
  {
    text: '公開期間（本番）',
    value: 'productionDate',
    width: '27%',
    class: tableHeaderClass,
  },
  {
    text: '状態',
    value: 'status',
    width: '8%',
    class: tableHeaderClass,
    sortable: false,
  },
  {
    text: '有効/無効',
    value: 'isEnabled',
    width: '11%',
    class: tableHeaderClass,
    sortable: false,
  },
  // 行アイコン
  {
    text: '',
    value: 'actions',
    sortable: false,
    width: '4%',
    class: tableHeaderClass,
    align: 'end',
  },
];

// リアクティブな変数の型定義
export type ReactiveData = {
  schoolListItem: TableSchool[]; // 学校一覧情報
  total: number; // 全件数
  tabs: any;
  selectedItem?: TableSchool;
  isOpenDialog: boolean;
  openedItemId: number | undefined;
  startDate: string; // 一括変更
  endDate: string;
  publishType: number | undefined;
  editTargets: number[];
  isLoading: boolean;
};

type Props = {
  organizationId: number;
};

export default defineComponent({
  components: { TableFilter, Reset, DatePicker, Typography, Dialog },
  setup(_, ctx) {
    const props = {
      organizationId: ctx.attrs['organization-id'],
    } as Props;

    const state: ReactiveData = reactive<ReactiveData>({
      schoolListItem: [], // 学校一覧情報
      total: 0, // 全件数
      tabs: 0,
      selectedItem: undefined, // テーブル上部のフィルター表示
      isOpenDialog: false,
      openedItemId: undefined,
      startDate: '',
      endDate: '',
      publishType: undefined,
      editTargets: [],
      isLoading: false,
    });

    const {
      tableState,
      fetchTableData,
      pagination,
      tableOptions,
      updateOptions,
    } = useTable<TableSchool, ResponseSchool, SchoolListFilter>({
      fetch: async (query: { [k: string]: unknown }) => {
        return (
          await axios.get(endPoints.listSchools.endPoint, {
            params: {
              ...query,
              organization_type_id: props.organizationId,
            },
          })
        ).data;
      },
      initialFilterFactory: schoolFilterFactory,
    });

    const { state: dialogState, openDialog, closeDialog } = useDialog();
    const { changeEnabled, cancellationPrivate } = useSchool();

    const setOpenedItemId = (id: number | undefined) => {
      state.openedItemId = id;
    };

    const actionsDialogStyle = computed(() => {
      return dialogState.isOpen ? { display: 'none' } : {};
    });

    const handleFetch = async () => {
      state.isLoading = true;
      const res = await fetchTableData(tableState.filter);

      const parsed = toTableSchools(res?.items || []);

      state.schoolListItem = parsed;

      state.total = res?.total || 0;

      state.isLoading = false;
    };

    watch(tableOptions, handleFetch);

    const handleSelect = (items: TableSchool[]) => {
      const isRemoveAll =
        tableState.selected.length - pagination.itemsPerPage === items.length;

      if (isRemoveAll) {
        tableState.selected = [];
      } else if (items.length === pagination.itemsPerPage) {
        tableState.selected = state.schoolListItem;
      } else {
        tableState.selected = items;
      }
    };

    const removeFilterItem = (key: string) => {
      const itemKey = key as keyof SchoolListFilter;

      tableState.filter[itemKey].value = undefined;

      if (tableState.filter[itemKey].rangeProps) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        tableState.filter[itemKey].rangeProps!.value = undefined;
      }
      handleFetch();
    };

    const setFilter = (newFilter: SchoolListFilter) => {
      tableState.filter = newFilter;
      handleFetch();
    };

    const handleChangeEnabled = async (item: TableSchool) => {
      dialogState.isProgress = true;
      await changeEnabled({
        organization_ids: item.id.toString(),
        is_enabled: item.isEnabled === '有効' ? 0 : 1,
      });
      dialogState.isProgress = false;
      dialogState.isCompleted = true;
      handleFetch();
    };

    const handleChangeForcedDisabled = (item: TableSchool) => {
      cancellationPrivate({
        organization_ids: item.id.toString(),
      }).then(handleFetch);
    };

    /*** computed ***/
    const selectedIds = computed(() =>
      tableState.selected.map((item) => item.id),
    );

    const selectedNames = computed(() =>
      tableState.selected.map((item) => item.name),
    );

    const createTo = (key: keyof TableSchool, item?: TableSchool) => {
      const id = item?.id ? item.id : selectedIds.value;
      const name = item?.name ? item.name : selectedNames.value;

      const publish_type = key === 'productionDate' ? 1 : 2;

      const base = `/admin/result-publication/period-edit/${id}?publish_type=${publish_type}&organization_names=${name}`;

      if (item) {
        const [startDate, endDate] = item[key].toString().split('~');
        const dateQuery = createDateQuery(startDate, endDate);

        return base + dateQuery;
      } else if (tableState.selected.length === 1) {
        const [startDate, endDate] = tableState.selected[0][key]
          ?.toString()
          .split('~');
        const dateQuery = createDateQuery(startDate, endDate);

        return base + dateQuery;
      } else {
        return base;
      }
    };

    const rehearsalTo = (item?: TableSchool) => {
      return createTo('rehearsalDate', item);
    };

    const createDateQuery = (startDate: string, endDate: string) => {
      const startDateQuery = startDate ? `&startdate_at=${startDate}` : '';
      const endDateQuery = endDate ? `&enddate_at=${endDate}` : '';
      return `${startDateQuery}${endDateQuery}`;
    };

    const productionTo = (item?: TableSchool) => {
      return createTo('productionDate', item);
    };

    const rehearsalToComputed = computed(() => rehearsalTo());
    const productionToComputed = computed(() => productionTo());

    const confirmMessage = computed(() =>
      state.selectedItem?.isEnabled === '有効'
        ? '学校を無効にしますか？'
        : '学校を有効にしますか？',
    );

    const schoolListItem = computed(() => {
      const index = (pagination.page - 1) * pagination.itemsPerPage;
      return state.schoolListItem.slice(index, index + pagination.itemsPerPage);
    });

    return {
      ...props,
      schoolListItem,
      schoolHeaders,
      state,
      tableState,
      pagination,
      tableOptions,
      handleSelect,
      updateOptions,
      setFilter,
      removeFilterItem,
      handleFetch,
      handleChangeEnabled,
      handleChangeForcedDisabled,
      selectedIds,
      selectedNames,
      footerProps: tableFooterProps,
      rehearsalTo,
      productionTo,
      rehearsalToComputed,
      productionToComputed,
      dialogState,
      setOpenedItemId,
      actionsDialogStyle,
      openDialog: (item: TableSchool) => {
        state.selectedItem = item;
        openDialog();
        setOpenedItemId(undefined);
      },
      closeDialog: () => {
        state.selectedItem = undefined;
        state.isOpenDialog = false;
        closeDialog();
        setOpenedItemId(undefined);
      },
      confirmMessage,
    };
  },
});
